// src/design-system/FieldsModal.tsx
import { useEffect, useState } from "react";

import { errorMessage } from "@/api/errors";

import { Field, Input, Select, Textarea } from "./Field";
import { FormModal } from "./FormModal";

export type FieldDef = {
  name: string;
  label: string;
  type?: "text" | "number" | "date" | "select" | "textarea" | "tel" | "password";
  options?: { value: string; label: string }[];
  required?: boolean;
  step?: string;
  hint?: string;
  ltr?: boolean;
  defaultValue?: string;
};
export type Values = Record<string, string>;

/** نافذة نموذج تصريحية للحقول البسيطة: التحقق من الإلزامي هنا، وأخطاء الخادم تظهر تحت الأزرار. */
export function FieldsModal({
  open, title, fields, onClose, onSubmit, submitLabel, validate, width,
}: {
  open: boolean; title: string; fields: FieldDef[]; onClose: () => void; submitLabel?: string; width?: number;
  onSubmit: (values: Values) => Promise<unknown>; validate?: (values: Values) => Record<string, string> | null;
}) {
  const initial = () => Object.fromEntries(fields.map((f) => [f.name, f.defaultValue ?? ""]));
  const [values, setValues] = useState<Values>(initial);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setValues(initial()); setErrors({}); setError(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const found: Record<string, string> = {};
    fields.forEach((f) => { if (f.required && !values[f.name]?.trim()) found[f.name] = "هذا الحقل إلزامي"; });
    Object.assign(found, validate?.(values) ?? {});
    setErrors(found);
    if (Object.keys(found).length) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(values);
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const set = (n: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setValues((v) => ({ ...v, [n]: e.target.value }));

  return (
    <FormModal open={open} title={title} onClose={onClose} onSubmit={submit} submitLabel={submitLabel} submitting={busy} error={error} width={width}>
      {fields.map((f) => {
        const id = `f-${f.name}`;
        const common = { id, value: values[f.name] ?? "", onChange: set(f.name), invalid: !!errors[f.name] };
        return (
          <Field key={f.name} label={f.label} htmlFor={id} error={errors[f.name]} hint={f.hint}>
            {f.type === "select" ? (
              <Select {...common}><option value="">اختر…</option>{f.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</Select>
            ) : f.type === "textarea" ? (
              <Textarea {...common} />
            ) : (
              <Input {...common} type={f.type ?? "text"} step={f.step} className={f.ltr ? "mono ltr !text-start" : ""} />
            )}
          </Field>
        );
      })}
    </FormModal>
  );
}
